import { env } from 'config'

// A web session keeps its access token only in this module's memory; the
// refresh token lives in an HttpOnly cookie on the API host that page scripts
// can't read. A reloaded tab starts without an access token and refreshes for
// one. The backend only issues sessions when its web_sessions_health_portal
// flag is on and the request comes from a registered Health Portal origin;
// everywhere else sign-in keeps returning the long-lived token.

export const WEB_SESSION_HEADER = 'Web-Session'
export const WEB_SESSION_GRANT = 'grant'

const ACCESS_TOKEN_PREFIX = 'nsw_at_'

// Not a credential: records that this browser may hold a refresh cookie, so
// tabs signed in with the long-lived token don't send a refresh on every load.
const SESSION_MARKER_STORAGE_KEY = 'webSession'

// Renew this long before expiry so a token doesn't lapse in flight.
const RENEW_BEFORE_EXPIRY_MS = 60_000

// Use a fresh token for at least this long, so a client clock running ahead of
// the server can't make every request refresh first.
const MIN_TOKEN_USE_MS = 30_000

// Statuses with which the refresh endpoint reports that the session can't be
// used: expired or revoked (401), not accepted from this origin (403), or web
// sessions turned off for the portal (404). Anything else, such as a 429 or
// 5xx, may pass, so the session is kept.
const SESSION_ENDED_STATUSES = [401, 403, 404]

interface IAccessToken {
  value: string
  renewAt: number
}

let accessToken: IAccessToken | null = null
let pendingRefresh: Promise<string | null> | null = null
let pendingExchange: Promise<void> | null = null
let pendingLogout: Promise<void> | null = null

// How long a sign-in waits for an earlier logout to finish first.
const LOGOUT_WAIT_MS = 5_000

// Bumped whenever the session is cleared or replaced, so a request started for
// an earlier session can't store its credentials over the current state.
let generation = 0

const sessionUrl = (action: 'session' | 'refresh' | 'logout') =>
  new URL(`/auth/web/health_portal/${action}`, env.REACT_APP_API_ENDPOINT).toString()

const post = (action: 'session' | 'refresh' | 'logout', body: Record<string, unknown> = {}) =>
  fetch(sessionUrl(action), {
    method: 'POST',
    credentials: 'include',
    // Lets a logout finish when the page navigates away right after it.
    keepalive: action === 'logout',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })

// Stores the credentials in a response, unless the session it was requested
// for has since been cleared or replaced. Resolves to the current token.
const storeAccessToken = async (response: Response, requestedFor: number) => {
  const { accessToken: value, expiresAt } = await response.json()
  if (generation !== requestedFor) return accessToken?.value ?? null

  accessToken = {
    value,
    renewAt: Math.max(Date.parse(expiresAt) - RENEW_BEFORE_EXPIRY_MS, Date.now() + MIN_TOKEN_USE_MS)
  }
  localStorage.setItem(SESSION_MARKER_STORAGE_KEY, 'true')
  return value as string
}

const startGeneration = () => {
  generation += 1
  accessToken = null
  pendingRefresh = null
  pendingExchange = null
  return generation
}

export const isWebSessionAccessToken = (token?: string | null) =>
  !!token?.startsWith(ACCESS_TOKEN_PREFIX)

export const hasWebSession = () =>
  !!accessToken || localStorage.getItem(SESSION_MARKER_STORAGE_KEY) === 'true'

// Forgets the session in this tab without contacting the backend.
export const clearWebSession = () => {
  startGeneration()
  localStorage.removeItem(SESSION_MARKER_STORAGE_KEY)
}

// A logout that finished after this exchange would clear the new session's
// cookie, so it goes first unless it stalls.
const waitForPendingLogout = () =>
  pendingLogout &&
  Promise.race([pendingLogout, new Promise((resolve) => setTimeout(resolve, LOGOUT_WAIT_MS))])

const exchange = async (grantToken: string, requestedFor: number) => {
  await waitForPendingLogout()
  const response = await post('session', { grantToken })
  if (!response.ok) {
    if (generation === requestedFor) clearWebSession()
    throw new Error(`Web session exchange failed with status ${response.status}`)
  }

  if ((await storeAccessToken(response, requestedFor)) === null) {
    throw new Error('Web session ended during the exchange')
  }
}

// Exchanges the single-use grant from a sign-in for a session. Until it
// completes, refreshes wait for it instead of rotating the previous session's
// cookie, which the exchange replaces.
export const exchangeWebSessionGrant = (grantToken: string) => {
  const requestedFor = startGeneration()
  const pending = exchange(grantToken, requestedFor).finally(() => {
    if (pendingExchange === pending) pendingExchange = null
  })
  pendingExchange = pending

  return pending
}

// Rotates the refresh cookie for a new access token. Resolves to null when the
// session has ended (expired, revoked, signed out or the backend flag turned
// off); rejects on a network or server failure, which leaves the session in
// place for a later retry. Concurrent callers in a tab share one request.
// Other tabs share the cookie, and the backend tolerates their refreshing at
// the same moment.
export const refreshWebSession = (): Promise<string | null> => {
  if (pendingExchange) {
    return pendingExchange.then(
      () => accessToken?.value ?? null,
      () => null
    )
  }
  if (pendingRefresh) return pendingRefresh
  if (!hasWebSession()) return Promise.resolve(null)

  const requestedFor = generation
  const refresh = post('refresh')
    .then((response) => {
      if (generation !== requestedFor) return accessToken?.value ?? null
      if (response.ok) return storeAccessToken(response, requestedFor)
      if (!SESSION_ENDED_STATUSES.includes(response.status)) {
        throw new Error(`Web session refresh failed with status ${response.status}`)
      }

      clearWebSession()
      return null
    })
    .finally(() => {
      if (pendingRefresh === refresh) pendingRefresh = null
    })
  pendingRefresh = refresh

  return refresh
}

const currentAccessToken = () =>
  accessToken && Date.now() < accessToken.renewAt ? accessToken.value : null

// The access token to send, refreshed first when it is about to expire or this
// tab has none yet. Waits for a sign-in in progress. Null without a web session.
export const getWebSessionAccessToken = async () => {
  const current = currentAccessToken()
  if (current) return current
  if (!hasWebSession() && !pendingExchange) return null

  return refreshWebSession()
}

// A replacement for an access token the backend rejected: the unexpired one
// another request already refreshed to, or a new one.
export const renewWebSessionAccessToken = (rejectedToken: string) => {
  const current = currentAccessToken()
  if (current && current !== rejectedToken) return Promise.resolve(current)

  return refreshWebSession()
}

// Forgets the session in this tab at once, then revokes it and clears its
// cookie. Never rejects: sign-out goes ahead locally even when the backend
// can't be reached.
export const endWebSession = () => {
  const hadSession = hasWebSession()
  clearWebSession()
  if (!hadSession) return Promise.resolve()

  const logout = post('logout')
    .then(
      () => undefined,
      () => undefined
    )
    .finally(() => {
      if (pendingLogout === logout) pendingLogout = null
    })
  pendingLogout = logout

  return logout
}
